import { chain, get } from 'lodash';
import { mapState } from 'pinia';
import ActivitiesDB from 'src/fb/activities-db';
import { auth } from 'src/store/auth';
import { sessions } from 'src/store/sessions';
import { logEvent } from 'src/utils/LogEvent';

export default {
  data() {
    return {
      eventsFired: [],
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'currentClient']),
    ...mapState(sessions, ['activeSession']),
  },

  methods: {
    resetActivities() {
      this.eventsFired = [];
    }, // resetActivities()

    async addActivity(
      event,
      onlyOnce = true,
      eventInfo = null,
      activityInfo = {},
      saveToDb = true,
      domain = 'session',
      session = null,
    ) {
      if (this.eventsFired.includes(event) && onlyOnce) {
        // Only record activities once per session
        return;
      }

      const activity = {
        domain,
        originalSession: get(
          session,
          'originalSessionId',
          get(
            this.activeSession,
            'originalSessionId',
            get(this.activeSession, 'id', null),
          ),
        ),
        session: get(session, 'id', get(this.activeSession, 'id', null)),
        client: get(this.currentClient, 'id', null),
        user: this.userDetails.userId,
        event,
        alias: get(this.currentClient, 'alias', ''),
        ...activityInfo,
      };

      if (eventInfo !== null) {
        activity.eventInfo = eventInfo;
      }

      if (saveToDb) {
        const activitiesDb = new ActivitiesDB();

        try {
          await activitiesDb.create(activity);
        } catch (err) {
          console.error(err);

          const existingActivity = await activitiesDb.read(err.message.slice(err.message.lastIndexOf('/') + 1));

          logEvent(`Activities created error: ${err.message}`, {
            context: {
              activityInfo: {
                existingActivity,
                newActivity: activity,
              },
            },
          });
        }
      }

      this.eventsFired.push(event);
    }, // addActivity()

    getActivityEventLabel(activity, prefix = '') {
      switch (activity.event) {
        case 'sessionFinished':
          return prefix + this.$t('activityFeed.events.sessionFinished');
        case 'sessionNotesAdded':
          return prefix + this.$t('activityFeed.events.sessionNotesAdded');
        case 'programPackageInstallation':
          return `${prefix + this.$t('activityFeed.events.programPackageInstallation')
          } (${get(activity, ['eventInfo', 'packageName'])})`;
        case 'programCreation': {
          const userName = get(activity, ['eventInfo', 'userName'], null);
          const userInfo = userName ? ` ${this.$t('common.by')} ${userName}` : '';
          return `${prefix + this.$t('activityFeed.events.programCreation')
          } (${get(activity, ['eventInfo', 'programName'])})${userInfo}`;
        }
        case 'programModification': {
          const userName = get(activity, ['eventInfo', 'userName'], null);
          const userInfo = userName ? ` ${this.$t('common.by')} ${userName}` : '';
          return `${prefix + this.$t('activityFeed.events.programModification')
          } (${get(activity, ['eventInfo', 'programName'])})${userInfo}`;
        } case 'targetMastered':
          return prefix + this.$t('activityFeed.events.targetMastered');
        case 'targetReopened':
          return prefix + this.$t('activityFeed.events.targetReopened');
        default:
          return null;
      }
    }, // getActivityEventLabel()

    getActivityContent(activity) {
      switch (activity.event) {
        case 'programPackageInstallation':
          return this.getFormattedPackageInstallationNotes(activity);
        case 'programCreation':
          return null;
        case 'programModification':
          return this.getProgramModificationNotes(activity);
        case 'targetMastered':
        case 'targetReopened':
          return this.$t('activityFeed.targetChange', {
            targetLabel: get(activity, ['eventInfo', 'targetLabel']),
            programName: get(activity, ['eventInfo', 'programName']),
          });

        default: return null;
      }
    }, // getActivityContent()

    getActivityColor(activity) {
      switch (activity.event) {
        case 'programPackageInstallation':
        case 'programCreation':
          return 'primary';
        case 'programModification':
          return 'primary-variant';
        case 'targetMastered':
          return 'positive';
        case 'targetReopened':
          return 'negative';
        default: return 'primary';
      }
    }, // getActivityColor()

    getActivityIcon(activity) {
      switch (activity.event) {
        case 'sessionFinished':
          return 'schedule';
        case 'sessionNotesAdded':
          return 'speaker_notes';
        case 'programCreation':
          return 'add_box';
        case 'programModification':
          return 'archive';
        case 'programPackageInstallation':
          return 'inventory_2';
        case 'targetMastered':
          return 'emoji_events';
        case 'targetReopened':
          return 'school';
        default: return null;
      }
    }, // getActivityIcon()

    /**
     * Formats array of installed programs' names into comma-separated string
     *
     * @param {object} activity
     * @return {string}
     */
    getFormattedPackageInstallationNotes(activity) {
      const maxListSize = 10;
      const installedProgramsNames = get(activity, ['eventInfo', 'programsNames'], []);

      const notesStart = this.$t('activityFeed.notes.programPackageInstallationNotes');
      const listOfProgramsNames = chain(installedProgramsNames).take(maxListSize).join(', ').value();
      const notesEnd = installedProgramsNames.length > 10 ? '...' : '.';

      return `${notesStart} ${listOfProgramsNames}${notesEnd}`;
    },

    /**
     * @param {object} activity
     * @return {string}
     */
    getProgramModificationNotes(activity) {
      const statusChange = get(activity, ['eventInfo', 'statusChange']);
      return statusChange
        ? this.$t('activityFeed.notes.programModificationStatus', statusChange)
        : get(activity, ['eventInfo', 'notes'] || '');
    },

  },
};
